import { clsx } from 'clsx';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ActionDot } from '~/components/ActionDot';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import { HStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { useTrackNavigation } from '~/hooks/analytics/useTrackNavigation';
import styles from './NavigationSidebarSubmenuItem.module.scss';

interface Props {
  action?: () => void;
  label: string;
  href?: string;
  Icon?: React.FunctionComponent;
  notificationCount?: number;
  notificationTooltip?: string;
  onClick?: () => void;
  matchSubRoutes?: boolean;
}

const isItemActive = (pathname: string, href?: string, matchSubRoutes = false) => {
  if (!href) return false;

  if (pathname === href) return true;

  return matchSubRoutes && pathname.startsWith(`${href}/`);
};

export const NavigationSidebarSubmenuItem: React.FC<Props> = ({
  action,
  onClick,
  href,
  label,
  Icon,
  notificationCount,
  notificationTooltip,
  matchSubRoutes,
}) => {
  const location = useLocation();
  const isActive = isItemActive(location.pathname, href, matchSubRoutes);
  const trackNavigation = useTrackNavigation();

  if (href) {
    const handleLinkClick = () => {
      onClick?.();
      trackNavigation('sidebar', href);
    };

    return (
      <Link to={href} className={clsx(styles.container, { [styles.active!]: isActive })} onClick={handleLinkClick}>
        <SimpleTooltip
          theme="dark"
          placement="end bottom"
          offset={-15}
          crossOffset={-20}
          title={notificationTooltip}
          mode="div"
          delay={60}
        >
          <HStack align="center" gap={1}>
            <Text variant="body2" weight="regular">
              {label}
            </Text>
            {Icon && <Icon />}
            {!!notificationCount && <ActionDot />}
          </HStack>
        </SimpleTooltip>
      </Link>
    );
  }

  if (action) {
    const handleClick = () => {
      onClick?.();
      action();
    };

    return (
      <div className={styles.container} onClick={handleClick}>
        <SimpleTooltip
          theme="dark"
          placement="end bottom"
          offset={-15}
          crossOffset={-20}
          title={notificationTooltip}
          mode="div"
          delay={60}
        >
          <HStack align="center" gap={1}>
            <Text variant="body2" weight="regular">
              {label}
            </Text>
            {Icon && <Icon />}
            {!!notificationCount && <ActionDot />}
          </HStack>
        </SimpleTooltip>
      </div>
    );
  }
};
